import React, { useState, useEffect } from "react";
import { IoStarSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import API from "../../Utils/api";

// Category Skeleton
const CategorySkeleton = () => (
  <div className="flex items-center gap-3 border p-3 rounded-md animate-pulse">
    <div className="w-12 h-12 bg-gray-200 rounded flex-shrink-0" />
    <div className="h-3 bg-gray-200 rounded w-full" />
  </div>
);

// Product Card Skeleton
const ProductSkeleton = () => (
  <div className="bg-white shadow-md rounded-2xl p-4 flex items-center border animate-pulse">
    <div className="w-full h-[150px] bg-gray-200 rounded-xl flex-shrink-0" />
    <div className="flex flex-col w-full gap-3 mt-3 ml-2">
      <div className="h-5 bg-gray-200 rounded w-3/4 mt-2" />
      <div className="h-3 bg-gray-200 rounded w-2/3 mt-2" />
      <div className="h-3 bg-gray-200 rounded w-2/3 mt-2" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mt-2" />
      <div className="flex gap-1 mt-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-4 h-4 bg-gray-200 rounded-full" />
        ))}
      </div>
    </div>
  </div>
);

const Products = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [catLoading, setCatLoading] = useState(true);
  const [prodLoading, setProdLoading] = useState(true);
  const [catError, setCatError] = useState("");
  const [prodError, setProdError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    setCatLoading(true);
    setCatError("");
    try {
      const res = await API.get("/category/all");
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCatError("Failed to load categories.");
    } finally {
      setCatLoading(false);
    }
  };

  const fetchProducts = async () => {
    setProdLoading(true);
    setProdError("");
    try {
      const res = await API.get("/product/all");
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProdError("Failed to load products.");
    } finally {
      setProdLoading(false);
    }
  };

  // Filter products by selected category
  const filteredProducts =
    selectedCategory === "All"
      ? products
      : products.filter((p) => p.category?._id === selectedCategory);

  return (
    <>
      {/* ── Category Filter Section ── */}
      <div className="w-full px-4 sm:px-6 md:px-10 py-4 flex flex-col items-center bg-yellow-50/30">
        <h3 className="text-2xl md:text-4xl font-serif text-[#000000] mt-5">
          Explore Products By Category
        </h3>

        {/* Category Error */}
        {catError && !catLoading && (
          <div className="flex flex-col items-center py-8 text-center">
            <span className="text-3xl mb-2">😕</span>
            <p className="text-gray-500 text-sm">{catError}</p>
            <button
              onClick={fetchCategories}
              className="mt-3 px-5 py-1.5 bg-[#3A7138] text-white rounded-lg text-xs hover:bg-[#2d5a2b] transition"
            >
              Try Again
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-10 w-full">
          {catLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <CategorySkeleton key={i} />
            ))
          ) : (
            <>
              {/* All Button */}
              <div
                onClick={() => setSelectedCategory("All")}
                className={`flex items-center gap-3 border p-3 rounded-md cursor-pointer
                  transition duration-300 hover:scale-105 hover:shadow-lg
                  ${
                    selectedCategory === "All"
                      ? "border-[#3A7138] bg-[#3A7138]/10"
                      : "hover:border-[#3A7138]"
                  }`}
              >
                <div className="w-12 h-12 rounded bg-[#3A7138]/10 flex items-center justify-center text-[#3A7138] font-bold text-sm flex-shrink-0">
                  All
                </div>
                <span className="text-sm tracking-wide">All Products</span>
              </div>

              {/* Category Cards */}
              {categories.map((item) => (
                <div
                  key={item._id}
                  onClick={() => setSelectedCategory(item._id)}
                  className={`flex items-center gap-3 border p-3 rounded-md cursor-pointer
                    transition duration-300 hover:scale-105 hover:shadow-lg
                    ${
                      selectedCategory === item._id
                        ? "border-[#3A7138] bg-[#3A7138]/10"
                        : "hover:border-[#3A7138]"
                    }`}
                >
                  <img
                    src={item.image}
                    alt={item.category}
                    className="w-12 h-12 object-cover rounded flex-shrink-0"
                  />
                  <span className="text-sm tracking-wide line-clamp-1">
                    {item.category}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── Products Section ── */}
      <div className="px-4 sm:px-6 md:px-10 py-4 sm:py-8 w-full bg-yellow-50/30">
        <h3 className="text-2xl md:text-4xl font-serif text-center text-[#000000] mb-10">
          Explore Products
          {selectedCategory !== "All" &&
            categories.find((c) => c._id === selectedCategory) && (
              <span className="text-[#3A7138] text-2xl ml-3">
                — {categories.find((c) => c._id === selectedCategory)?.category}
              </span>
            )}
        </h3>

        {/* Product Error */}
        {prodError && !prodLoading && (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-600 font-medium">{prodError}</p>
            <button
              onClick={fetchProducts}
              className="mt-4 px-6 py-2 bg-[#3A7138] text-white rounded-xl text-sm hover:bg-[#2d5a2b] transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!prodLoading && !prodError && filteredProducts.length === 0 && (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="text-5xl mb-4">📦</span>
            <p className="text-gray-600 font-medium">No products found</p>
            <p className="text-gray-400 text-sm mt-1">
              {selectedCategory !== "All"
                ? "No products in this category yet"
                : "No products available yet"}
            </p>
            {selectedCategory !== "All" && (
              <button
                onClick={() => setSelectedCategory("All")}
                className="mt-3 px-5 py-1.5 bg-[#3A7138] text-white rounded-lg text-xs hover:bg-[#2d5a2b] transition"
              >
                View All Products
              </button>
            )}
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {prodLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <ProductSkeleton key={i} />
              ))
            : filteredProducts.map((item) => (
                <div
                  key={item._id}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate(`/products/${item._id}`);
                  }}
                  className="bg-white relative shadow-md rounded-2xl p-4 flex items-center border hover:border-[#3A7138] mb-5 transition hover:scale-105 duration-300 hover:shadow-xl hover:backdrop-blur-sm cursor-pointer"
                >
                  <img
                    src={item.images?.[0]}
                    alt={item.name}
                    className="w-full h-[150px] object-contain"
                  />
                  <span
                    className={`absolute top-1 right-1 px-3 py-1 text-xs rounded-2xl text-white ${
                      item.stock > 0 ? "bg-[#407B00]" : "bg-red-500"
                    }`}
                  >
                    {item.stock > 0 ? "In Stock" : "Out of Stock"}
                  </span>
                  <div className="flex flex-col w-full gap-3 mt-3">
                    <h3 className="text-xl font-semibold text-[#425340] mt-2">
                      {item.name}
                    </h3>
                    <p className="text-sm mt-2 text-[#425340]">
                      <span>Dosage: </span>
                      {item.dosage || "—"}
                    </p>
                    <p className="text-sm mt-2 text-[#425340]">
                      <span>Pack Size: </span>
                      {item.packSizes?.join(", ") || "—"}
                    </p>
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <IoStarSharp
                          key={i}
                          size={20}
                          className={
                            i < item.rating
                              ? "text-yellow-400"
                              : "text-gray-200"
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </>
  );
};

export default Products;
